import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import DeliveryAddressPicker, { deliveryErrorMessage, localizedPickerError, retryDeadline, validAddress, parseQuoteTimestamp } from '../components/DeliveryAddressPicker';
import MenuImage from '../components/MenuImage';
import { API_BASE_URL } from '../config/api';

const QUOTE_ERROR_COPY = {
  'Checkout quote is incomplete or does not match your selection.': {
    ar: 'تسعيرة الطلب غير مكتملة أو لا تطابق اختيارك.',
    ms: 'Sebut harga pesanan tidak lengkap atau tidak sepadan dengan pilihan anda.'
  },
  'Delivery quote is incomplete, expired, or does not match your address.': {
    ar: 'تسعيرة التوصيل غير مكتملة أو منتهية الصلاحية أو لا تطابق عنوانك.',
    ms: 'Sebut harga penghantaran tidak lengkap, tamat tempoh atau tidak sepadan dengan alamat anda.'
  },
  'Unable to calculate checkout total.': {
    ar: 'تعذّر حساب إجمالي الطلب.',
    ms: 'Jumlah pesanan tidak dapat dikira.'
  }
};

const localizedQuoteError = (safeMessage, language) => {
  if (language !== 'ar' && language !== 'ms') return safeMessage;
  const entry = QUOTE_ERROR_COPY[safeMessage];
  return entry
    ? entry[language]
    : localizedPickerError(safeMessage, language);
};

const ORDER_TYPES = {
  dine_in: 'Dine-in',
  pickup: 'Pickup',
  delivery: 'Delivery'
};
const MAX_QUANTITY = 100;
const MAX_LINES = 100;
const MONEY_FIELDS = ['subtotal', 'service_charge_amount', 'tax_amount', 'delivery_fee', 'total_amount'];
const DRAFT_REFERENCE = /^DM-[A-Za-z0-9_-]{32}$/;
// Draft-only policy: unusable, expired or unreasonable (>24h) Retry-After
// values fall back to the backend limiter's conservative 60-second window.
const draftRetryDeadline = response => {
  const now = Date.now();
  const fallback = now + 60000;
  const raw = response.headers?.get?.('Retry-After');
  if (typeof raw !== 'string') return fallback;
  const value = raw.trim();
  let delay;
  if (/^\d+$/.test(value)) {
    const seconds = Number(value);
    if (!Number.isSafeInteger(seconds)) return fallback;
    delay = seconds * 1000;
  } else {
    const asctimeFormat = /^[A-Z][a-z]{2} [A-Z][a-z]{2} [ \d]\d \d{2}:\d{2}:\d{2} \d{4}$/.test(value);
    // HTTP's asctime format has no suffix but always denotes GMT.
    const instant = Date.parse(asctimeFormat ? `${value} GMT` : value);
    if (!Number.isFinite(instant)) return fallback;
    const date = new Date(instant);
    const canonical = date.toUTCString();
    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const rfc850 = `${weekdays[date.getUTCDay()]}, ${canonical.slice(5, 7)}-${canonical.slice(8, 11)}-${canonical.slice(14, 16)} ${canonical.slice(17)}`;
    const asctime = `${canonical.slice(0, 3)} ${canonical.slice(8, 11)} ${String(date.getUTCDate()).padStart(2, ' ')} ${canonical.slice(17, 25)} ${date.getUTCFullYear()}`;
    // Reject permissive Date.parse inputs and normalized impossible dates.
    if (value !== canonical && value !== rfc850 && value !== asctime) return fallback;
    delay = instant - now;
  }
  return Number.isFinite(delay) && delay > 0 && delay <= 86400000 ? now + delay : fallback;
};
// The backend emits decimal amounts quantized to cents. Compare those cents
// exactly, without floating-point multiplication or repricing in the browser.
const moneyCents = value => {
  if (!Number.isFinite(value) || value < 0) return null;
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(String(value));
  if (!match) return null;
  const cents = Number(`${match[1]}${(match[2] || '').padEnd(2, '0')}`);
  return Number.isSafeInteger(cents) ? cents : null;
};
const sameCommercialQuote = (previous, current) =>
  typeof previous?.currency === 'string' && Boolean(previous.currency.trim()) &&
  previous.currency === current?.currency && MONEY_FIELDS.every(field => {
    const before = moneyCents(previous[field]);
    const after = moneyCents(current[field]);
    return before !== null && after !== null && before === after;
  });
// Decimal(str(number)) semantics, including scientific notation. BigInt keeps
// percentage checks exact; these calculations verify server values only.
const decimalRatio = value => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  const [mantissa, exponent = '0'] = String(value).toLowerCase().split('e');
  const [whole, fraction = ''] = mantissa.split('.');
  const scale = fraction.length - Number(exponent);
  const numerator = BigInt(whole + fraction);
  return scale >= 0 ? [numerator, 10n ** BigInt(scale)] : [numerator * 10n ** BigInt(-scale), 1n];
};
const roundHalfUp = (numerator, denominator) => (2n * numerator + denominator) / (2n * denominator);
// Python str.strip() includes NEL and excludes BOM; JS trim() differs.
const backendTrim = value => value.replace(/^[\u0009-\u000d\u001c-\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+|[\u0009-\u000d\u001c-\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+$/g, '');
const selectionKey = item => {
  if (!item || typeof item.menu_id !== 'string' || !/^[a-f0-9]{24}$/.test(item.menu_id) ||
      !Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > MAX_QUANTITY) return null;
  const variants = item.variants === undefined ? [] : item.variants;
  const instructions = item.special_instructions;
  if (!Array.isArray(variants) || variants.length > 20 ||
      variants.some(value => typeof value !== 'string' || !backendTrim(value) || Array.from(value).length > 100) ||
      (instructions !== undefined && instructions !== null && (typeof instructions !== 'string' || Array.from(instructions).length > 500))) return null;
  // The backend trims variant names, preserves their order and omits empty notes.
  return JSON.stringify([item.menu_id, item.quantity, variants.map(backendTrim), instructions || null]);
};
const validDraftItemsAndTotals = (items, requested, q, orderType) => {
  if (!Array.isArray(requested) || !requested.length || requested.length > MAX_LINES ||
      !Array.isArray(items) || items.length !== requested.length) return false;
  const remaining = new Map();
  for (const item of requested) {
    const key = selectionKey(item);
    if (key === null) return false;
    remaining.set(key, (remaining.get(key) || 0) + 1);
  }
  let subtotal = 0n;
  for (const item of items) {
    const key = selectionKey(item);
    const price = moneyCents(item?.price);
    const lineTotal = moneyCents(item?.line_total);
    if (key === null || !remaining.get(key) || typeof item.name !== 'string' ||
        price === null || lineTotal === null || BigInt(price) * BigInt(item.quantity) !== BigInt(lineTotal)) return false;
    remaining.set(key, remaining.get(key) - 1);
    subtotal += BigInt(lineTotal);
  }
  const cents = Object.fromEntries(MONEY_FIELDS.map(field => [field, BigInt(moneyCents(q[field]))]));
  if (subtotal !== cents.subtotal) return false;
  const tax = decimalRatio(q.tax_rate);
  if (!tax || roundHalfUp(subtotal * tax[0], tax[1] * 100n) !== cents.tax_amount) return false;
  let service = 0n;
  // Public draft serialization omits null fields, including nested metadata.
  if (q.service_charge_type === null || q.service_charge_type === undefined) {
    if (q.service_charge_value !== null && q.service_charge_value !== undefined) return false;
  } else {
    const value = decimalRatio(q.service_charge_value);
    if (!value) return false;
    if (q.service_charge_type === 'percentage') {
      if (q.service_charge_value > 100) return false;
      service = roundHalfUp(subtotal * value[0], value[1] * 100n);
    }
    else if (q.service_charge_type === 'fixed') service = roundHalfUp(value[0] * 100n, value[1]);
    else return false;
  }
  return service === cents.service_charge_amount &&
    (orderType === 'delivery' || cents.delivery_fee === 0n) &&
    subtotal + service + cents.tax_amount + cents.delivery_fee === cents.total_amount;
};
// 32 CSPRNG bytes as 43 unpadded base64url characters; no weaker fallback.
const newIdempotencyKey = () => {
  if (typeof globalThis.crypto?.getRandomValues !== 'function') throw new Error('Secure random unavailable');
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
// Sensitive recovery material, not authentication. Same-origin XSS can read it;
// shared-device/session restoration can retain it. Never persist order text or
// capabilities, and never infer cancellation or cross-tab exclusivity from it.
export const RECOVERY_STORAGE_KEY = 'unigate.publicDraftRecovery.v1';
const RECOVERY_LIFETIME = 30 * 60 * 1000;
const unresolvedRecovery = () => ({ version: 1, state: 'unresolved' });
const recoveryApiBase = () => {
  const url = new URL(API_BASE_URL);
  if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password || url.search || url.hash) throw new Error('recoveryStorage');
  return url.href.replace(/\/+$/, '');
};
const recoveryScope = restaurantId => ({ apiBase: recoveryApiBase(), restaurantId, method: 'POST',
  endpointPath: `${new URL(recoveryApiBase()).pathname.replace(/\/+$/, '')}/public/restaurants/${encodeURIComponent(restaurantId)}/order-drafts` });
export const validateRecoveryRecord = record => {
  if (!record || typeof record !== 'object' || Array.isArray(record) || record.version !== 1) throw new Error('recoveryInvalid');
  const fields = record.state === 'unresolved' ? ['version', 'state'] :
    ['version', 'state', 'apiBase', 'restaurantId', 'method', 'endpointPath', 'createdAtMs', 'recoverUntilMs', 'idempotencyKey', 'bindingDigest'];
  if (Object.keys(record).length !== fields.length || !fields.every(field => Object.hasOwn(record, field))) throw new Error('recoveryInvalid');
  if (record.state === 'unresolved') return record;
  if (record.state !== 'active' || typeof record.restaurantId !== 'string' ||
      !/^[A-Za-z0-9_-]{1,64}$/.test(record.restaurantId) ||
      !Number.isSafeInteger(record.createdAtMs) || !Number.isSafeInteger(record.recoverUntilMs) ||
      record.createdAtMs < 0 || record.createdAtMs > Date.now() ||
      record.recoverUntilMs <= record.createdAtMs || record.recoverUntilMs - record.createdAtMs > RECOVERY_LIFETIME ||
      typeof record.idempotencyKey !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(record.idempotencyKey) ||
      typeof record.bindingDigest !== 'string' || !/^[a-f0-9]{64}$/.test(record.bindingDigest)) throw new Error('recoveryInvalid');
  const scope = recoveryScope(record.restaurantId);
  if (!['apiBase', 'method', 'endpointPath'].every(field => record[field] === scope[field])) throw new Error('recoveryInvalid');
  return record;
};
const readRecoveryRecord = () => {
  let raw;
  try { raw = sessionStorage.getItem(RECOVERY_STORAGE_KEY); } catch { throw new Error('recoveryStorage'); }
  if (raw === null) return null;
  if (raw.length > 4096) throw new Error('recoveryInvalid');
  let record;
  try { record = JSON.parse(raw); } catch { throw new Error('recoveryInvalid'); }
  return validateRecoveryRecord(record);
};
const writeRecoveryRecord = record => {
  validateRecoveryRecord(record);
  try { sessionStorage.setItem(RECOVERY_STORAGE_KEY, JSON.stringify(record)); } catch { throw new Error('recoveryStorage'); }
  const retained = readRecoveryRecord();
  if (JSON.stringify(retained) !== JSON.stringify(record)) throw new Error('recoveryStorage');
  return retained;
};
export const recoveryBinding = async (record, body) => {
  if (typeof body !== 'string' || typeof globalThis.crypto?.subtle?.digest !== 'function') throw new Error('recoveryStorage');
  // The random key prevents cross-intent digest correlation, not dictionary
  // guesses by an attacker who can read this sensitive session record (XSS).
  const material = JSON.stringify([1, record.apiBase, record.method, record.endpointPath,
    record.restaurantId, record.idempotencyKey, body]);
  const hash = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(material));
  return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');
};
// Copies only the fields needed for handoff; the capability is never retained.
export const readDraft = (payload, { orderType, language, placeId, items }) => {
  const q = payload?.quote;
  // Validate calendar/offset before parsing; Python datetime has no year zero.
  // Microseconds truncate to milliseconds, never rounding the expiry up.
  const expiresAt = typeof payload?.expires_at === 'string' && !payload.expires_at.startsWith('0000-')
    ? parseQuoteTimestamp(payload.expires_at) : NaN;
  if (!payload || typeof payload.reference !== 'string' || !DRAFT_REFERENCE.test(payload.reference) ||
      payload.status !== 'AWAITING_HANDOFF' || payload.order_type !== orderType || payload.language !== language ||
      !q || q.order_type !== orderType || typeof q.currency !== 'string' || !q.currency.trim() ||
      !MONEY_FIELDS.every(field => moneyCents(q[field]) !== null) ||
      !Number.isFinite(expiresAt) || expiresAt <= Date.now() ||
      !validDraftItemsAndTotals(payload.items, items, q, orderType)) return null;
  if (orderType === 'delivery' && (payload.delivery_place_id !== placeId || typeof payload.delivery_address !== 'string' || !payload.delivery_address.trim())) return null;
  return {
    reference: payload.reference,
    orderType: payload.order_type,
    expiresAt,
    deliveryAddress: orderType === 'delivery' ? payload.delivery_address : null,
    items: payload.items.map(item => ({
      name: item.name,
      quantity: item.quantity,
      variants: item.variants === undefined ? [] : [...item.variants],
      specialInstructions: item.special_instructions ?? null
    })),
    quote: Object.fromEntries([['currency', q.currency], ...MONEY_FIELDS.map(field => [field, q[field]])])
  };
};
const buttonClass = 'min-h-[44px] rounded-xl px-4 py-2 font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:cursor-not-allowed disabled:opacity-50';
const primaryClass = `${buttonClass} bg-[#232D4B] text-white hover:bg-[#344269]`;
const currencyLabel = currency => ({
  MYR: 'RM',
  USD: '$',
  SGD: 'S$',
  EUR: '€',
  GBP: '£',
  SAR: '﷼',
  AED: 'د.إ',
  YER: '﷼'
})[currency] || currency;
const money = (value, currency) => `${currencyLabel(currency)} ${Number(value).toFixed(2)}`;
export const draftMessage = (draft, restaurantName) => {
  const q = draft.quote;
  return [
    'New Order',
    '',
    `Restaurant: ${restaurantName}`,
    `Order Type: ${ORDER_TYPES[draft.orderType]}`,
    `Reference: ${draft.reference}`,
    ...(draft.orderType === 'delivery' ? [`Delivery Address: ${draft.deliveryAddress}`] : []),
    '',
    ...draft.items.flatMap(item => [
      `${item.quantity} × ${item.name}${item.variants.length ? ` (${item.variants.join(', ')})` : ''}`,
      ...(item.specialInstructions !== null && item.specialInstructions !== ''
        ? [`  Instructions: ${item.specialInstructions}`] : [])
    ]),
    '',
    `Subtotal: ${money(q.subtotal, q.currency)}`,
    `Service Charge: ${money(q.service_charge_amount, q.currency)}`,
    `SST: ${money(q.tax_amount, q.currency)}`,
    `Delivery Fee: ${money(q.delivery_fee, q.currency)}`,
    `Total: ${money(q.total_amount, q.currency)}`,
  ];
};
const localizedField = (entry, field, language) => {
  const base = entry?.[field];
  const fallback = typeof base === 'string' ? base : '';
  if (language !== 'ar' && language !== 'ms') return fallback;
  const translation = entry?.[`${field}_${language}`];
  return typeof translation === 'string' && translation.trim()
    ? translation
    : fallback;
};

const PUBLIC_MENU_COPY = {
  en: {
    cartA11y: {
      add: 'Add {name} to cart',
      summary: 'Cart summary',
      quantityFor: 'Quantity for {name}',
      decrease: 'Decrease quantity of {name}',
      quantityOf: 'Quantity of {name}',
      increase: 'Increase quantity of {name}',
      remove: 'Remove {name} from cart'
    },
    stickyCart: {
      oneItem: 'item',
      manyItems: 'items',
      updating: 'Updating total\u2026',
      totalUnavailable: 'Total unavailable',
      deliveryUnavailable: 'Delivery total unavailable',
      chooseOrder: 'Choose order type for total',
      viewCart: 'View cart'
    },
    deliveryMetrics: {
      distance: 'Distance',
      duration: 'Estimated duration',
      minuteUnit: 'min'
    },
    quoteStatus: {
      chooseOrder: 'Choose an order type to see your total.',
      chooseAddress: 'Select and verify a delivery address to see your total.',
      cooldown: 'Requests are paused. Please wait before retrying.',
      expired: 'Delivery quote expired. Refresh before sending.',
      refresh: 'Refresh delivery quote',
      calculating: 'Calculating checkout total\u2026',
      retry: 'Retry total'
    },
    quoteLabels: {
      subtotal: 'Subtotal',
      service_charge_amount: 'Service Charge',
      tax_amount: 'SST',
      delivery_fee: 'Delivery Fee',
      total_amount: 'Total'
    },
    tableLabel: 'Table Number',
    tablePlaceholder: 'e.g. 12, A3, Patio 2',
    tableHint: 'We\u2019ll include this table number in your WhatsApp order.',
    whatsappUnavailable: 'WhatsApp ordering is unavailable for this restaurant.',
    orderWhatsapp: 'Order via WhatsApp',
    whatsappHelp: 'Opens WhatsApp so you can send your order to the restaurant.',
    draft: {
      creating: 'Preparing your order\u2026',
      failed: 'We could not prepare your order. Please try again.',
      conflict: 'This order can no longer be prepared. Review your selection and try again.',
      rateLimited: 'Too many requests. Please wait a moment and try again.',
      cooldown: 'Order preparation is paused. Please wait before trying again.',
      invalid: 'The order confirmation was incomplete. Please try again.',
      expired: 'Your prepared order expired. Please try again.',
      stale: 'Your selection changed. Prepare your order again.',
      navigationAttempted: 'If WhatsApp did not open, try Continue to WhatsApp again.',
      navigationFailed: 'WhatsApp could not be opened. Please try Continue to WhatsApp again.',
      recoveryNotice: 'An earlier order attempt may exist. Your cart was not automatically restored.',
      recoveryRetry: 'Retry earlier attempt',
      recoveryNew: 'Start a new order',
      recoveryWarning: 'Starting a new order discards local recovery. An earlier draft may still exist; it is not cancelled.',
      recoveryStorage: 'We could not safely retain recovery data. Check browser storage before retrying this order attempt.',
      recoveryInvalid: 'Saved recovery data is invalid. Choose explicitly whether to start a new order.',
      recoveryMismatch: 'This selection does not match the earlier attempt. Correct it or explicitly start a new order.',
      recoveryRequired: 'Rebuild the exact earlier selection, then choose Retry earlier attempt.',
      recoveryExpired: 'Local recovery has expired. An earlier draft may still exist. Choose explicitly whether to start a new order.',
      changed: 'Order amounts or currency changed. Review and accept the updated details to continue.',
      previous: 'Previous',
      updated: 'Updated',
      accept: 'Accept new total',
      continue: 'Continue to WhatsApp'
    },
    cartHeading: 'Your cart',
    cartEmpty: 'Add a dish to get started.',
    each: 'each',
    remove: 'Remove',
    orderQuestion: 'How would you like your order?',
    orderTypeLabels: {
      dine_in: 'Dine-in',
      pickup: 'Pickup',
      delivery: 'Delivery'
    },
    language: 'Language',
    welcome: 'Welcome to our menu',
    browseMenu: 'Browse menu',
    findDish: 'Find something delicious',
    searchPlaceholder: 'Search dishes or descriptions',
    menuCategories: 'Menu categories',
    allItems: 'All items',
    noMatches: 'No dishes match your search in this category.',
    noItems: 'No menu items are available right now.',
    limitReached: 'Limit reached',
    add: 'Add +',
    inYourCart: 'in your cart'
  },
  ar: {
    cartA11y: {
      add: '\u0623\u0636\u0641 {name} \u0625\u0644\u0649 \u0627\u0644\u0633\u0644\u0629',
      summary: '\u0645\u0644\u062e\u0635 \u0627\u0644\u0633\u0644\u0629',
      quantityFor: '\u0627\u0644\u0643\u0645\u064a\u0629 \u0644\u0644\u0635\u0646\u0641 {name}',
      decrease: '\u062a\u0642\u0644\u064a\u0644 \u0643\u0645\u064a\u0629 {name}',
      quantityOf: '\u0643\u0645\u064a\u0629 {name}',
      increase: '\u0632\u064a\u0627\u062f\u0629 \u0643\u0645\u064a\u0629 {name}',
      remove: '\u0625\u0632\u0627\u0644\u0629 {name} \u0645\u0646 \u0627\u0644\u0633\u0644\u0629'
    },
    stickyCart: {
      oneItem: '\u0635\u0646\u0641',
      manyItems: '\u0623\u0635\u0646\u0627\u0641',
      updating: '\u062c\u0627\u0631\u064d \u062a\u062d\u062f\u064a\u062b \u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a\u2026',
      totalUnavailable: '\u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a \u063a\u064a\u0631 \u0645\u062a\u0627\u062d',
      deliveryUnavailable: '\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u062a\u0648\u0635\u064a\u0644 \u063a\u064a\u0631 \u0645\u062a\u0627\u062d',
      chooseOrder: '\u0627\u062e\u062a\u0631 \u0646\u0648\u0639 \u0627\u0644\u0637\u0644\u0628 \u0644\u0645\u0639\u0631\u0641\u0629 \u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a',
      viewCart: '\u0639\u0631\u0636 \u0627\u0644\u0633\u0644\u0629'
    },
    deliveryMetrics: {
      distance: '\u0627\u0644\u0645\u0633\u0627\u0641\u0629',
      duration: '\u0627\u0644\u0645\u062f\u0629 \u0627\u0644\u062a\u0642\u062f\u064a\u0631\u064a\u0629',
      minuteUnit: '\u062f\u0642\u064a\u0642\u0629'
    },
    quoteStatus: {
      chooseOrder: '\u0627\u062e\u062a\u0631 \u0646\u0648\u0639 \u0627\u0644\u0637\u0644\u0628 \u0644\u0639\u0631\u0636 \u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a.',
      chooseAddress: '\u0627\u062e\u062a\u0631 \u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u062a\u0648\u0635\u064a\u0644 \u0648\u062a\u062d\u0642\u0642 \u0645\u0646\u0647 \u0644\u0639\u0631\u0636 \u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a.',
      cooldown: '\u062a\u0645 \u0625\u064a\u0642\u0627\u0641 \u0637\u0644\u0628\u0627\u062a \u0627\u0644\u062a\u0633\u0639\u064a\u0631 \u0645\u0624\u0642\u062a\u064b\u0627. \u064a\u064f\u0631\u062c\u0649 \u0627\u0644\u0627\u0646\u062a\u0638\u0627\u0631 \u0642\u0628\u0644 \u0625\u0639\u0627\u062f\u0629 \u0627\u0644\u0645\u062d\u0627\u0648\u0644\u0629.',
      expired: '\u0627\u0646\u062a\u0647\u062a \u0635\u0644\u0627\u062d\u064a\u0629 \u062a\u0633\u0639\u064a\u0631\u0629 \u0627\u0644\u062a\u0648\u0635\u064a\u0644. \u062d\u062f\u0651\u062b\u0647\u0627 \u0642\u0628\u0644 \u0627\u0644\u0625\u0631\u0633\u0627\u0644.',
      refresh: '\u062a\u062d\u062f\u064a\u062b \u062a\u0633\u0639\u064a\u0631\u0629 \u0627\u0644\u062a\u0648\u0635\u064a\u0644',
      calculating: '\u062c\u0627\u0631\u064d \u062d\u0633\u0627\u0628 \u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0637\u0644\u0628\u2026',
      retry: '\u0625\u0639\u0627\u062f\u0629 \u0645\u062d\u0627\u0648\u0644\u0629 \u062d\u0633\u0627\u0628 \u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a'
    },
    quoteLabels: {
      subtotal: '\u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0641\u0631\u0639\u064a',
      service_charge_amount: '\u0631\u0633\u0648\u0645 \u0627\u0644\u062e\u062f\u0645\u0629',
      tax_amount: 'SST',
      delivery_fee: '\u0631\u0633\u0648\u0645 \u0627\u0644\u062a\u0648\u0635\u064a\u0644',
      total_amount: '\u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a'
    },
    tableLabel: '\u0631\u0642\u0645 \u0627\u0644\u0637\u0627\u0648\u0644\u0629',
    tablePlaceholder: '\u0645\u062b\u0627\u0644: 12\u060c A3\u060c Patio 2',
    tableHint: '\u0633\u0646\u064f\u0636\u0645\u0651\u0646 \u0631\u0642\u0645 \u0627\u0644\u0637\u0627\u0648\u0644\u0629 \u0641\u064a \u0637\u0644\u0628\u0643 \u0639\u0628\u0631 WhatsApp.',
    whatsappUnavailable: '\u0627\u0644\u0637\u0644\u0628 \u0639\u0628\u0631 WhatsApp \u063a\u064a\u0631 \u0645\u062a\u0627\u062d \u0644\u0647\u0630\u0627 \u0627\u0644\u0645\u0637\u0639\u0645.',
    orderWhatsapp: '\u0627\u0637\u0644\u0628 \u0639\u0628\u0631 WhatsApp',
    whatsappHelp: '\u064a\u0641\u062a\u062d WhatsApp \u0644\u062a\u062a\u0645\u0643\u0646 \u0645\u0646 \u0625\u0631\u0633\u0627\u0644 \u0637\u0644\u0628\u0643 \u0625\u0644\u0649 \u0627\u0644\u0645\u0637\u0639\u0645.',
    draft: {
      creating: 'جارٍ تجهيز طلبك…',
      failed: 'تعذّر تجهيز طلبك. يرجى المحاولة مرة أخرى.',
      conflict: 'لم يعد بالإمكان تجهيز هذا الطلب. راجع اختيارك وحاول مرة أخرى.',
      rateLimited: 'طلبات كثيرة جدًا. يرجى الانتظار قليلًا ثم المحاولة مرة أخرى.',
      cooldown: 'تم إيقاف تجهيز الطلب مؤقتًا. يرجى الانتظار قبل المحاولة مجددًا.',
      invalid: 'تأكيد الطلب غير مكتمل. يرجى المحاولة مرة أخرى.',
      expired: 'انتهت صلاحية طلبك المجهّز. يرجى المحاولة مرة أخرى.',
      stale: 'تغيّرت اختياراتك. يرجى تجهيز طلبك مجددًا.',
      navigationAttempted: 'إذا لم يفتح WhatsApp، حاول المتابعة إلى WhatsApp مجددًا.',
      navigationFailed: 'تعذّر فتح WhatsApp. يرجى محاولة المتابعة إلى WhatsApp مجددًا.',
      recoveryNotice: 'قد توجد محاولة طلب سابقة. لم تتم استعادة سلتك تلقائيًا.',
      recoveryRetry: 'إعادة محاولة الطلب السابق',
      recoveryNew: 'بدء طلب جديد',
      recoveryWarning: 'بدء طلب جديد يحذف الاستعادة المحلية فقط. قد تظل مسودة سابقة موجودة؛ ولن تُلغى.',
      recoveryStorage: 'تعذّر حفظ بيانات الاستعادة بأمان. تحقق من تخزين المتصفح قبل إعادة محاولة هذا الطلب.',
      recoveryInvalid: 'بيانات الاستعادة غير صالحة. اختر صراحةً إن كنت تريد بدء طلب جديد.',
      recoveryMismatch: 'هذه الاختيارات لا تطابق المحاولة السابقة. صحّحها أو ابدأ طلبًا جديدًا صراحةً.',
      recoveryRequired: 'أعد تحديد الاختيارات السابقة بدقة، ثم اختر إعادة محاولة الطلب السابق.',
      recoveryExpired: 'انتهت مهلة الاستعادة المحلية. قد توجد مسودة سابقة. اختر صراحةً إن كنت تريد بدء طلب جديد.',
      changed: 'تغيّرت مبالغ الطلب أو العملة. راجع التفاصيل المحدّثة واقبلها للمتابعة.',
      previous: 'السابق',
      updated: 'المحدّث',
      accept: 'اقبل الإجمالي الجديد',
      continue: 'المتابعة إلى WhatsApp'
    },
    cartHeading: '\u0633\u0644\u062a\u0643',
    cartEmpty: '\u0623\u0636\u0641 \u0637\u0628\u0642\u064b\u0627 \u0644\u0644\u0628\u062f\u0621.',
    each: '\u0644\u0644\u0648\u062d\u062f\u0629',
    remove: '\u0625\u0632\u0627\u0644\u0629',
    orderQuestion: '\u0643\u064a\u0641 \u062a\u0631\u063a\u0628 \u0641\u064a \u0627\u0633\u062a\u0644\u0627\u0645 \u0637\u0644\u0628\u0643\u061f',
    orderTypeLabels: {
      dine_in: '\u062a\u0646\u0627\u0648\u0644 \u0627\u0644\u0637\u0639\u0627\u0645 \u0641\u064a \u0627\u0644\u0645\u0637\u0639\u0645',
      pickup: '\u0627\u0633\u062a\u0644\u0627\u0645 \u0645\u0646 \u0627\u0644\u0645\u0637\u0639\u0645',
      delivery: '\u062a\u0648\u0635\u064a\u0644'
    },
    language: '\u0627\u0644\u0644\u063a\u0629',
    welcome: '\u0623\u0647\u0644\u0627\u064b \u0628\u0643\u0645 \u0641\u064a \u0642\u0627\u0626\u0645\u062a\u0646\u0627',
    browseMenu: '\u062a\u0635\u0641\u062d \u0627\u0644\u0642\u0627\u0626\u0645\u0629',
    findDish: '\u0627\u0628\u062d\u062b \u0639\u0646 \u0637\u0628\u0642 \u064a\u0639\u062c\u0628\u0643',
    searchPlaceholder: '\u0627\u0628\u062d\u062b \u0639\u0646 \u0637\u0628\u0642 \u0623\u0648 \u0648\u0635\u0641',
    menuCategories: '\u0641\u0626\u0627\u062a \u0627\u0644\u0642\u0627\u0626\u0645\u0629',
    allItems: '\u062c\u0645\u064a\u0639 \u0627\u0644\u0623\u0635\u0646\u0627\u0641',
    noMatches: '\u0644\u0627 \u062a\u0648\u062c\u062f \u0623\u0637\u0628\u0627\u0642 \u062a\u0637\u0627\u0628\u0642 \u0628\u062d\u062b\u0643 \u0641\u064a \u0647\u0630\u0647 \u0627\u0644\u0641\u0626\u0629.',
    noItems: '\u0644\u0627 \u062a\u0648\u062c\u062f \u0623\u0635\u0646\u0627\u0641 \u0645\u062a\u0627\u062d\u0629 \u062d\u0627\u0644\u064a\u064b\u0627.',
    limitReached: '\u062a\u0645 \u0628\u0644\u0648\u063a \u0627\u0644\u062d\u062f \u0627\u0644\u0623\u0642\u0635\u0649',
    add: '\u0623\u0636\u0641 +',
    inYourCart: '\u0641\u064a \u0633\u0644\u062a\u0643'
  },
  ms: {
    cartA11y: {
      add: 'Tambah {name} ke troli',
      summary: 'Ringkasan troli',
      quantityFor: 'Kuantiti untuk {name}',
      decrease: 'Kurangkan kuantiti {name}',
      quantityOf: 'Kuantiti {name}',
      increase: 'Tambah kuantiti {name}',
      remove: 'Buang {name} daripada troli'
    },
    stickyCart: {
      oneItem: 'item',
      manyItems: 'item',
      updating: 'Sedang mengemas kini jumlah\u2026',
      totalUnavailable: 'Jumlah tidak tersedia',
      deliveryUnavailable: 'Jumlah penghantaran tidak tersedia',
      chooseOrder: 'Pilih jenis pesanan untuk melihat jumlah',
      viewCart: 'Lihat troli'
    },
    deliveryMetrics: {
      distance: 'Jarak',
      duration: 'Anggaran masa',
      minuteUnit: 'minit'
    },
    quoteStatus: {
      chooseOrder: 'Pilih jenis pesanan untuk melihat jumlah keseluruhan.',
      chooseAddress: 'Pilih dan sahkan alamat penghantaran untuk melihat jumlah keseluruhan.',
      cooldown: 'Permintaan dijeda buat sementara. Sila tunggu sebelum mencuba lagi.',
      expired: 'Sebut harga penghantaran telah tamat tempoh. Muat semula sebelum menghantar.',
      refresh: 'Muat semula sebut harga penghantaran',
      calculating: 'Sedang mengira jumlah pesanan\u2026',
      retry: 'Cuba kira jumlah semula'
    },
    quoteLabels: {
      subtotal: 'Jumlah kecil',
      service_charge_amount: 'Caj perkhidmatan',
      tax_amount: 'SST',
      delivery_fee: 'Caj penghantaran',
      total_amount: 'Jumlah keseluruhan'
    },
    tableLabel: 'Nombor meja',
    tablePlaceholder: 'cth. 12, A3, Patio 2',
    tableHint: 'Nombor meja ini akan disertakan dalam pesanan WhatsApp anda.',
    whatsappUnavailable: 'Pesanan melalui WhatsApp tidak tersedia untuk restoran ini.',
    orderWhatsapp: 'Pesan melalui WhatsApp',
    whatsappHelp: 'Membuka WhatsApp supaya anda boleh menghantar pesanan kepada restoran.',
    draft: {
      creating: 'Menyediakan pesanan anda\u2026',
      failed: 'Pesanan anda tidak dapat disediakan. Sila cuba lagi.',
      conflict: 'Pesanan ini tidak lagi dapat disediakan. Semak pilihan anda dan cuba lagi.',
      rateLimited: 'Terlalu banyak permintaan. Sila tunggu sebentar dan cuba lagi.',
      cooldown: 'Penyediaan pesanan dijeda. Sila tunggu sebelum mencuba lagi.',
      invalid: 'Pengesahan pesanan tidak lengkap. Sila cuba lagi.',
      expired: 'Pesanan yang disediakan telah tamat tempoh. Sila cuba lagi.',
      stale: 'Pilihan anda telah berubah. Sediakan pesanan anda semula.',
      navigationAttempted: 'Jika WhatsApp tidak dibuka, cuba Teruskan ke WhatsApp sekali lagi.',
      navigationFailed: 'WhatsApp tidak dapat dibuka. Cuba Teruskan ke WhatsApp sekali lagi.',
      recoveryNotice: 'Percubaan pesanan terdahulu mungkin wujud. Troli anda tidak dipulihkan secara automatik.',
      recoveryRetry: 'Cuba semula percubaan terdahulu',
      recoveryNew: 'Mulakan pesanan baharu',
      recoveryWarning: 'Pesanan baharu membuang pemulihan setempat sahaja. Draf terdahulu mungkin masih wujud dan tidak dibatalkan.',
      recoveryStorage: 'Data pemulihan tidak dapat disimpan dengan selamat. Semak storan pelayar sebelum mencuba pesanan ini semula.',
      recoveryInvalid: 'Data pemulihan tidak sah. Pilih secara jelas sama ada hendak memulakan pesanan baharu.',
      recoveryMismatch: 'Pilihan ini tidak sepadan dengan percubaan terdahulu. Betulkan pilihan atau mulakan pesanan baharu secara jelas.',
      recoveryRequired: 'Bina semula pilihan terdahulu dengan tepat, kemudian cuba semula percubaan terdahulu.',
      recoveryExpired: 'Tempoh pemulihan setempat tamat. Draf terdahulu mungkin masih wujud. Pilih secara jelas sama ada hendak memulakan pesanan baharu.',
      changed: 'Amaun pesanan atau mata wang telah berubah. Semak dan terima butiran terkini untuk meneruskan.',
      previous: 'Sebelum',
      updated: 'Terkini',
      accept: 'Terima jumlah baharu',
      continue: 'Teruskan ke WhatsApp'
    },
    cartHeading: 'Troli anda',
    cartEmpty: 'Tambah hidangan untuk bermula.',
    each: 'setiap satu',
    remove: 'Buang',
    orderQuestion: 'Bagaimanakah anda mahu menerima pesanan anda?',
    orderTypeLabels: {
      dine_in: 'Makan di sini',
      pickup: 'Ambil sendiri',
      delivery: 'Penghantaran'
    },
    language: 'Bahasa',
    welcome: 'Selamat datang ke menu kami',
    browseMenu: 'Lihat menu',
    findDish: 'Cari hidangan kegemaran anda',
    searchPlaceholder: 'Cari hidangan atau penerangan',
    menuCategories: 'Kategori menu',
    allItems: 'Semua item',
    noMatches: 'Tiada hidangan sepadan dengan carian anda dalam kategori ini.',
    noItems: 'Tiada item menu tersedia buat masa ini.',
    limitReached: 'Had dicapai',
    add: 'Tambah +',
    inYourCart: 'dalam troli anda'
  }
};

// Include all selection configuration in identity, including future fields.
// Array order stays intact: conservatively split rather than lose configuration.
const canonical = value =>
  Array.isArray(value)
    ? value.map(canonical)
    : value && typeof value === 'object'
      ? Object.fromEntries(
          Object.keys(value).sort().map(key => [key, canonical(value[key])])
        )
      : value;
const plainSelection = {
  variants: [],
  special_instructions: null
};
const lineIdentity = (menuId, configuration) => JSON.stringify([menuId, canonical(configuration)]);

export function addCartItem(cart, item, selection = {}) {
  const configuration = canonical({
    ...plainSelection,
    ...selection
  });
  const id = lineIdentity(item.id, configuration);
  const existing = cart.find(line => line.id === id);
  if (existing) {
    if (existing.quantity >= MAX_QUANTITY) return cart;
    return cart.map(line => line.id === id ? {
      ...line,
      quantity: line.quantity + 1
    } : line);
  }
  if (cart.length >= MAX_LINES) return cart;
  return [...cart, {
    id,
    menu_id: item.id,
    name: item.name,
    // Descriptive only; never sent in quote requests.
    catalog_unit_price: item.price,
    quantity: 1,
    configuration
  }];
}

export function validateTable(value) {
  if (!value.trim()) return 'Enter your table number for dine-in.';
  if (value.trim().length > 40) return 'Use 40 characters or fewer for your table number.';
  if (/[\u0000-\u001f\u007f-\u009f\u2028\u2029]/.test(value)) return 'Use a single line without control characters.';
  return '';
}

export default function PublicMenu() {
  const { restaurantId } = useParams();
  const draftCooldowns = useRef(new Map());
  // A route change resets all scoped state before any effects run.
  return <RestaurantMenu
    key={restaurantId}
    restaurantId={restaurantId}
    draftCooldowns={draftCooldowns}
  />;
}

function RestaurantMenu({ restaurantId, draftCooldowns }) {
  const [menuLanguage, setMenuLanguage] = useState('en');
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState('');
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  const [cart, setCart] = useState([]);
  const [orderType, setOrderType] = useState('');
  const [table, setTable] = useState('');
  const [tableTouched, setTableTouched] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [quoteState, setQuoteState] = useState(null);
  const [quoteAttempt, setQuoteAttempt] = useState(0);
  const [destination, setDestination] = useState(null);
  const selectionRevision = useRef(0);
  const rateLimitedQuote = useRef(null);
  const previousQuoteIdentity = useRef(null);
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  const onCooldown = useCallback(deadline => {
    if (mounted.current) setCooldownUntil(current => Math.max(current, deadline));
  }, []);
  const onDestinationChange = useCallback(value => {
    selectionRevision.current += 1;
    setDestination(value ? { ...value, selectionRevision: selectionRevision.current } : null);
    setQuoteState(null);
  }, []);
  useLayoutEffect(() => {
    if (cart.length === 0 && orderType === 'delivery') onDestinationChange(null);
  }, [cart.length, orderType, onDestinationChange]);
  useEffect(() => {
    if (!cooldownUntil) return;
    let timer;
    const checkDeadline = () => {
      const remaining = cooldownUntil - Date.now();
      if (remaining <= 0) { setCooldownUntil(0); setNow(Date.now()); }
      else timer = setTimeout(checkDeadline, Math.min(2147483647, remaining));
    };
    checkDeadline();
    return () => clearTimeout(timer);
  }, [cooldownUntil]);
  const [checkoutVisible, setCheckoutVisible] = useState(false);
  const [checkoutFocused, setCheckoutFocused] = useState(false);
  const checkoutSectionRef = useRef(null);
  const checkoutRef = useRef(null);
  const tableRef = useRef(null);
  const base = `${API_BASE_URL}/public/restaurants/${encodeURIComponent(restaurantId)}`;
  const [draftCooldownUntil, setDraftCooldownUntil] = useState(() => draftCooldowns.current.get(base) || 0);
  const draftCoolingDown = draftCooldownUntil > Date.now();
  useEffect(() => {
    if (!draftCooldownUntil) return;
    let timer;
    const checkDeadline = () => {
      const remaining = draftCooldownUntil - Date.now();
      if (remaining > 0) timer = setTimeout(checkDeadline, Math.min(2147483647, remaining));
      else {
        if (draftCooldowns.current.get(base) === draftCooldownUntil) draftCooldowns.current.delete(base);
        setDraftCooldownUntil(0);
      }
    };
    checkDeadline();
    return () => clearTimeout(timer);
  }, [base, draftCooldownUntil, draftCooldowns]);

  useEffect(() => {
    if (!catalog || !checkoutSectionRef.current || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => {
      setCheckoutVisible(entry.isIntersecting);
    }, { rootMargin: '0px 0px -96px 0px' });
    observer.observe(checkoutSectionRef.current);
    return () => observer.disconnect();
  }, [catalog]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setCatalogError('');
    const read = async path => {
      const response = await fetch(`${base}${path}`, {
        signal: controller.signal
      });
      if (!response.ok) throw new Error('This menu is unavailable. Please try again.');
      return response.json();
    };
    Promise.all([read(''), read('/categories'), read('/menu')]).then(([restaurant, categories, items]) => {
      if (
        restaurant?.id !== restaurantId ||
        typeof restaurant?.name !== 'string' ||
        !Array.isArray(categories) ||
        !Array.isArray(items)
      ) {
        throw new Error('This menu could not be loaded. Please try again.');
      }
      if (active) setCatalog({
        restaurant,
        categories,
        items
      });
    }).catch(error => {
      if (active && error.name !== 'AbortError') setCatalogError(error.message);
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [base, restaurantId, catalogAttempt]);

  const quoteRequest = useMemo(() => cart.length && orderType && (orderType !== 'delivery' || (destination?.restaurantId === restaurantId)) ? JSON.stringify({
    ...(orderType === 'delivery' ? { delivery_place_id: destination.placeId } : {}),
    order_type: orderType,
    items: cart.map(line => ({
      menu_id: line.menu_id,
      quantity: line.quantity,
      variants: line.configuration.variants,
      ...(line.configuration.special_instructions ? {
        special_instructions: line.configuration.special_instructions
      } : {})
    }))
  }) : null, [cart, orderType, destination, restaurantId]);
  const quoteIdentity = quoteRequest ? JSON.stringify([restaurantId, quoteRequest, orderType === 'delivery' ? destination?.selectionRevision : null]) : null;

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    // Suppression belongs to a request identity; cooldown belongs to the client.
    if (previousQuoteIdentity.current !== quoteIdentity) {
      previousQuoteIdentity.current = quoteIdentity;
      rateLimitedQuote.current = null;
    }
    if (quoteIdentity && rateLimitedQuote.current === quoteIdentity) return () => controller.abort();
    if (!quoteRequest || Date.now() < cooldownUntil) return () => controller.abort();
    setQuoteState(null);
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`${base}/checkout/quote`, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json'
          },
          body: quoteRequest
        });
        const payload = await response.json();
        const deadline = retryDeadline(response, payload);
        if (deadline) onCooldown(deadline);
        if (!active) return;
        if (deadline) rateLimitedQuote.current = quoteIdentity;
        if (!response.ok) throw new Error(deliveryErrorMessage(payload?.code));
        if (
          payload?.order_type !== JSON.parse(quoteRequest).order_type ||
          typeof payload?.currency !== 'string' ||
          !payload.currency.trim() ||
          !MONEY_FIELDS.every(field =>
            typeof payload[field] === 'number' &&
            Number.isFinite(payload[field]) &&
            payload[field] >= 0
          )
        ) {
          throw new Error('Checkout quote is incomplete or does not match your selection.');
        }
        if (orderType === 'delivery') {
          const generated = parseQuoteTimestamp(payload.quote_generated_at);
          const expires = parseQuoteTimestamp(payload.quote_expires_at);
          if (payload.delivery_place_id !== destination.placeId || !validAddress(payload.delivery_address) ||
              typeof payload.delivery_distance_km !== 'number' || !Number.isFinite(payload.delivery_distance_km) || payload.delivery_distance_km < 0 ||
              !(payload.delivery_duration_minutes == null || (Number.isSafeInteger(payload.delivery_duration_minutes) && payload.delivery_duration_minutes >= 0)) ||
              !Number.isFinite(generated) || !Number.isFinite(expires) ||
              expires <= generated || expires <= Date.now()) {
            throw new Error('Delivery quote is incomplete, expired, or does not match your address.');
          }
        }
        if (active) setQuoteState({
          request: quoteIdentity,
          quote: payload
        });
      } catch (error) {
        if (active && error.name !== 'AbortError') setQuoteState({
          request: quoteIdentity,
          error: [deliveryErrorMessage(''), ...['DELIVERY_RATE_LIMITED', 'DELIVERY_DESTINATION_NOT_FOUND', 'INVALID_DELIVERY_DESTINATION', 'INVALID_PLACES_REQUEST', 'DELIVERY_OUT_OF_RANGE', 'MIN_ORDER_NOT_MET', 'DELIVERY_DISABLED', 'INVALID_DELIVERY_SETTINGS', 'INVALID_CHECKOUT_CONFIGURATION', 'INVALID_SERVICE_CHARGE_SETTINGS', 'DELIVERY_ROUTE_STALE', 'RESTAURANT_UNAVAILABLE', 'DELIVERY_PROVIDER_RATE_LIMITED', 'DELIVERY_ROUTE_UNAVAILABLE', 'DELIVERY_PROVIDER_TIMEOUT', 'INVALID_PLACE_DETAILS', 'INVALID_PLACE_SUGGESTIONS', 'INVALID_PROVIDER_RESPONSE', 'DELIVERY_PROVIDER_REJECTED', 'INVALID_DELIVERY_ROUTE', 'DELIVERY_ROUTE_REQUIRED'].map(deliveryErrorMessage), 'Checkout quote is incomplete or does not match your selection.', 'Delivery quote is incomplete, expired, or does not match your address.'].includes(error.message) ? error.message : 'Unable to calculate checkout total.'
        });
      }
    }, 180);
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [base, quoteRequest, quoteIdentity, quoteAttempt, orderType, destination, cooldownUntil, onCooldown]);

  // Never display or hand off an offer for a different selection, even before cleanup.
  const currentQuote = quoteIdentity && quoteState?.request === quoteIdentity ? quoteState : null;
  const expiresAt = orderType === 'delivery' && currentQuote?.quote ? parseQuoteTimestamp(currentQuote.quote.quote_expires_at) : Infinity;
  const expired = expiresAt <= Math.max(now, Date.now());
  useEffect(() => {
    if (!Number.isFinite(expiresAt)) return;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(2147483647, Math.max(0, expiresAt - Date.now())));
    return () => clearTimeout(timer);
  }, [expiresAt]);
  const quote = expired ? null : currentQuote?.quote;
  const quoteError = currentQuote?.error;
  const quoteLoading = Boolean(quoteRequest && !currentQuote && Date.now() >= cooldownUntil);
  const ui = PUBLIC_MENU_COPY[menuLanguage] || PUBLIC_MENU_COPY.en;
  const restaurant = catalog?.restaurant;
  const currency = restaurant?.currency || 'MYR';
  const count = cart.reduce((total, line) => total + line.quantity, 0);
  const cartDisplayName = line => {
    const item = catalog?.items.find(entry => entry.id === line.menu_id);
    const localizedName = item && localizedField(item, 'name', menuLanguage);
    return localizedName || line.name;
  };
  const tableError = orderType === 'dine_in' && tableTouched ? validateTable(table) : '';

  const groups = useMemo(() => {
    if (!catalog) return [];
    const byId = new Map(catalog.categories.map(cat => [cat.id, {
      ...cat,
      items: []
    }]));
    const byName = new Map([...byId.values()].map(cat => [cat.name, cat]));
    const other = {
      id: 'uncategorized',
      name: 'More from the menu',
      items: []
    };
    for (const item of catalog.items) {
      (byId.get(item.category_id) || byName.get(item.category) || other).items.push(item);
    }
    return [...byId.values(), other].filter(group => group.items.length);
  }, [catalog]);

  const visibleGroups = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return groups.filter(group => category === 'all' || category === group.id).map(group => ({
      ...group,
      items: group.items.filter(item => [
        item.name,
        item.description,
        localizedField(item, 'name', menuLanguage),
        localizedField(item, 'description', menuLanguage)
      ].some(value => typeof value === 'string' && value.toLocaleLowerCase().includes(query)))
    })).filter(group => group.items.length);
  }, [groups, category, search, menuLanguage]);

  const changeQuantity = (id, delta) => setCart(current => current.map(line => line.id === id ? {
    ...line,
    quantity: Math.min(MAX_QUANTITY, line.quantity + delta)
  } : line).filter(line => line.quantity > 0));
  const draftRun = useRef(null);
  const draftKey = useRef(null);
  const intentRecord = useRef(null);
  const [recovery, setRecovery] = useState({ loaded: false, record: null, error: '' });
  const refreshRecovery = useCallback(() => {
    try {
      let record = readRecoveryRecord();
      const expired = record?.state === 'active' && record.recoverUntilMs <= Date.now();
      if (expired) record = writeRecoveryRecord(unresolvedRecovery());
      setRecovery({ loaded: true, record, error: expired ? 'recoveryExpired' : '' });
    } catch (error) {
      let record = null;
      if (error?.message === 'recoveryInvalid') {
        try { record = writeRecoveryRecord(unresolvedRecovery()); } catch { /* Remain blocked. */ }
      }
      setRecovery({ loaded: true, record, error: error?.message === 'recoveryInvalid' ? 'recoveryInvalid' : 'recoveryStorage' });
    }
  }, []);
  useEffect(() => { refreshRecovery(); }, [refreshRecovery]);
  useEffect(() => {
    if (recovery.record?.state !== 'active') return;
    let timer;
    const check = () => {
      const remaining = recovery.record.recoverUntilMs - Date.now();
      if (remaining > 0) timer = setTimeout(check, Math.min(2147483647, remaining));
      else { intentRecord.current = null; draftKey.current = null; refreshRecovery(); }
    };
    check();
    return () => clearTimeout(timer);
  }, [recovery.record, refreshRecovery]);
  const [draftState, setDraftState] = useState(null);
  const draftIdentity = quoteIdentity ? JSON.stringify([quoteIdentity, menuLanguage]) : null;
  const latestDraftIdentity = useRef(draftIdentity);
  // Any change to the selection, language or restaurant abandons in-flight and pending drafts.
  useLayoutEffect(() => {
    latestDraftIdentity.current = draftIdentity;
    setDraftState(previous => previous && previous.identity !== draftIdentity &&
      (previous.phase === 'ready' || previous.phase === 'review')
      ? { identity: draftIdentity, phase: 'error', error: 'stale' } : null);
    return () => {
      latestDraftIdentity.current = null;
      draftRun.current?.controller.abort();
      draftRun.current = null;
    };
  }, [draftIdentity]);
  const currentDraft = draftIdentity && draftState?.identity === draftIdentity ? draftState : null;
  const draftBusy = currentDraft?.phase === 'creating';
  const draftReview = currentDraft?.phase === 'review';
  const draftReady = currentDraft?.phase === 'ready';
  const canHandoff = Boolean(cart.length && orderType && quote && restaurant?.whatsapp_number &&
    (orderType !== 'delivery' || (destination?.restaurantId === restaurantId &&
      destination.selectionRevision === selectionRevision.current && quote.delivery_place_id === destination.placeId)));
  const openWhatsapp = (lines, number) => window.open(
    `https://wa.me/${number.replace('+', '')}?text=${encodeURIComponent(lines.join('\n'))}`,
    '_blank',
    'noopener,noreferrer'
  );
  const finishDraft = (draft, identity) => {
    if (latestDraftIdentity.current !== identity) return;
    if (draft.expiresAt <= Date.now()) {
      draftKey.current = null;
      setDraftState({ identity, phase: 'error', error: 'expired' });
      return;
    }
    setDraftState({ identity, phase: 'ready', draft, restaurantId,
      destination: restaurant.whatsapp_number });
  };
  const continueDraft = () => {
    const number = restaurant?.whatsapp_number;
    if (!draftReady || !currentDraft?.draft || latestDraftIdentity.current !== currentDraft.identity ||
        currentDraft.restaurantId !== restaurantId || restaurant?.id !== restaurantId ||
        currentDraft.draft.orderType !== orderType || currentDraft.destination !== number ||
        typeof number !== 'string' || !/^\+?\d+$/.test(number)) {
      setDraftState({ identity: draftIdentity, phase: 'error', error: 'stale' });
      return;
    }
    if (currentDraft.draft.expiresAt <= Date.now()) {
      draftKey.current = null;
      setDraftState({ identity: currentDraft.identity, phase: 'error', error: 'expired' });
      return;
    }
    try {
      // Keep navigation synchronous with native button activation. A null return
      // is inconclusive with noopener; retain the prepared draft for retries.
      openWhatsapp(draftMessage(currentDraft.draft, restaurant.name), number);
      setDraftState({ ...currentDraft, navigation: 'navigationAttempted' });
    } catch {
      setDraftState({ ...currentDraft, navigation: 'navigationFailed' });
    }
  };
  const startNewOrder = () => {
    if (draftRun.current) return;
    try {
      sessionStorage.removeItem(RECOVERY_STORAGE_KEY);
      if (sessionStorage.getItem(RECOVERY_STORAGE_KEY) !== null) throw new Error('recoveryStorage');
      intentRecord.current = null;
      draftKey.current = null;
      setDraftState(null);
      setRecovery({ loaded: true, record: null, error: '' });
    } catch { setRecovery(previous => ({ ...previous, error: 'recoveryStorage' })); }
  };
  const createDraft = async (explicitRecovery = false) => {
    if (draftRun.current || (draftCooldowns.current.get(base) || 0) > Date.now()) return;
    const identity = draftIdentity;
    if (!recovery.loaded || recovery.error) {
      setDraftState({ identity, phase: 'error', error: recovery.error || 'recoveryStorage' });
      return;
    }
    if (!MONEY_FIELDS.every(field => moneyCents(quote[field]) !== null)) {
      setDraftState({ identity, phase: 'error', error: 'invalid' });
      return;
    }
    const request = JSON.parse(quoteRequest);
    const body = JSON.stringify({ language: menuLanguage, ...request });
    const displayedQuote = Object.fromEntries(['currency', ...MONEY_FIELDS].map(field => [field, quote[field]]));
    const context = {
      orderType,
      language: menuLanguage,
      placeId: orderType === 'delivery' ? destination.placeId : null,
      items: request.items
    };
    const controller = new AbortController();
    draftRun.current = { identity, controller };
    setDraftState({ identity, phase: 'creating' });
    let posted = false;
    try {
      let retained = readRecoveryRecord();
      if (explicitRecovery && !retained) throw new Error('recoveryRequired');
      if (retained?.state === 'unresolved') throw new Error('recoveryExpired');
      if (retained && retained.recoverUntilMs <= Date.now()) {
        const marker = writeRecoveryRecord(unresolvedRecovery());
        setRecovery({ loaded: true, record: marker, error: 'recoveryExpired' });
        throw new Error('recoveryExpired');
      }
      const scope = recoveryScope(restaurantId);
      if (retained && !Object.keys(scope).every(field => retained[field] === scope[field])) throw new Error('recoveryMismatch');
      if (retained && !explicitRecovery && intentRecord.current?.idempotencyKey !== retained.idempotencyKey) throw new Error('recoveryRequired');
      if (!retained && intentRecord.current) throw new Error('recoveryStorage');
      const createdAt = Date.now();
      const candidate = retained || { version: 1, state: 'active', ...scope,
        createdAtMs: createdAt, recoverUntilMs: createdAt + RECOVERY_LIFETIME,
        idempotencyKey: newIdempotencyKey() };
      const digest = await recoveryBinding(candidate, body);
      if (draftRun.current?.controller !== controller) return;
      if (retained && retained.bindingDigest !== digest) throw new Error('recoveryMismatch');
      if (intentRecord.current && (intentRecord.current.idempotencyKey !== candidate.idempotencyKey ||
          intentRecord.current.bindingDigest !== digest)) throw new Error('recoveryMismatch');
      if (JSON.stringify(readRecoveryRecord()) !== JSON.stringify(retained)) throw new Error('recoveryStorage');
      if (candidate.recoverUntilMs <= Date.now()) {
        const marker = writeRecoveryRecord(unresolvedRecovery());
        intentRecord.current = null;
        draftKey.current = null;
        setRecovery({ loaded: true, record: marker, error: 'recoveryExpired' });
        throw new Error('recoveryExpired');
      }
      retained = writeRecoveryRecord({ ...candidate, bindingDigest: digest });
      intentRecord.current = retained;
      draftKey.current = { identity, key: retained.idempotencyKey };
      setRecovery({ loaded: true, record: retained, error: '' });
      posted = true;
      const result = await fetch(`${base}/order-drafts`, {
        method: 'POST',
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': draftKey.current.key },
        body
      });
      if (draftRun.current?.controller !== controller) return;
      if (result.status === 429) {
        const deadline = draftRetryDeadline(result);
        draftCooldowns.current.set(base, deadline);
        setDraftCooldownUntil(deadline);
        setDraftState({ identity, phase: 'error', error: 'rateLimited' });
        return;
      }
      const payload = await result.json().catch(() => null);
      if (draftRun.current?.controller !== controller) return;
      if (!result.ok) {
        // Keep the saved intent even on conflict; only an explicit new-order
        // decision may replace its key. A response is not cancellation proof.
        setDraftState({ identity, phase: 'error', error: result.status === 429 ? 'rateLimited' : result.status === 409 ? 'conflict' : 'failed' });
        return;
      }
      const draft = readDraft(payload, context);
      if (!draft) {
        // The draft may have persisted; an unchanged retry must reuse its key.
        setDraftState({ identity, phase: 'error', error: 'invalid' });
        return;
      }
      const saved = readRecoveryRecord();
      if (!saved || saved.state !== 'active' || saved.idempotencyKey !== retained.idempotencyKey ||
          saved.bindingDigest !== retained.bindingDigest) throw new Error('recoveryStorage');
      const deadline = Math.min(saved.recoverUntilMs, draft.expiresAt);
      const shortened = deadline <= Date.now() ? unresolvedRecovery() : { ...saved, recoverUntilMs: deadline };
      const verified = writeRecoveryRecord(shortened);
      intentRecord.current = verified.state === 'active' ? verified : null;
      setRecovery({ loaded: true, record: verified, error: '' });
      if (sameCommercialQuote(displayedQuote, draft.quote)) finishDraft(draft, identity);
      else setDraftState({ identity, phase: 'review', draft, previousQuote: displayedQuote });
    } catch (error) {
      if (controller.signal.aborted || draftRun.current?.controller !== controller) return;
      const recoveryError = ['recoveryStorage', 'recoveryInvalid', 'recoveryRequired', 'recoveryMismatch', 'recoveryExpired'].includes(error?.message)
        ? error.message : !posted ? 'recoveryStorage' : 'failed';
      if (recoveryError === 'recoveryInvalid') refreshRecovery();
      setDraftState({ identity, phase: 'error', error: recoveryError });
    } finally {
      if (draftRun.current?.controller === controller) draftRun.current = null;
    }
  };
  const sendOrder = () => {
    if (orderType === 'dine_in' && validateTable(table)) {
      setTableTouched(true);
      tableRef.current?.focus();
      return;
    }
    if (!canHandoff || draftBusy || draftReview || draftReady || (orderType !== 'dine_in' && draftCoolingDown)) return;
    if (orderType === 'delivery') {
      const expires = parseQuoteTimestamp(quote.quote_expires_at);
      if (!destination || destination.restaurantId !== restaurantId || destination.selectionRevision !== selectionRevision.current ||
          !Number.isFinite(expires) || expires <= Date.now()) { setNow(Date.now()); return; }
    }
    if (orderType !== 'dine_in') { createDraft(); return; }
    const text = [
      'New Order',
      '',
      `Restaurant: ${restaurant.name}`,
      `Order Type: ${ORDER_TYPES[orderType]}`,
      `Table: ${table.trim()}`,
      '',
      ...cart.map(line => `${line.quantity} × ${line.name}`),
      '',
      `Subtotal: ${money(quote.subtotal, quote.currency)}`,
      `Service Charge: ${money(quote.service_charge_amount, quote.currency)}`,
      `SST: ${money(quote.tax_amount, quote.currency)}`,
      `Delivery Fee: ${money(quote.delivery_fee, quote.currency)}`,
      `Total: ${money(quote.total_amount, quote.currency)}`,
    ];
    openWhatsapp(text, restaurant.whatsapp_number);
  };
  const acceptDraftTotal = () => {
    if (draftReview && draftRun.current === null) finishDraft(currentDraft.draft, currentDraft.identity);
  };

  if (!catalog) return <main className="mx-auto max-w-3xl p-6">
    <h1 className="text-2xl font-bold">Restaurant menu</h1>
    {catalogError ? <div
      role="alert"
      className="mt-4 space-y-3"
    >
      <p>{catalogError}</p>
      <button
        className={primaryClass}
        onClick={() => setCatalogAttempt(value => value + 1)}
      >Try again</button>
    </div> : <p
      role="status"
      className="mt-4"
    >Loading restaurant menu…</p>}
  </main>;
  return <main lang={menuLanguage} dir={menuLanguage === 'ar' ? 'rtl' : 'ltr'} className="mx-auto max-w-6xl px-4 pt-6 pb-32 sm:px-6 sm:pt-10">
    <div className="mb-4 flex justify-end">
      <label htmlFor="public-menu-language" className="flex items-center gap-2 text-sm font-semibold">
        <span>{ui.language}</span>
        <select
          id="public-menu-language"
          value={menuLanguage}
          onChange={event => setMenuLanguage(event.target.value)}
          className="min-h-[44px] rounded-xl border border-input bg-background px-3"
        >
          <option value="en">English</option>
          <option value="ar">&#1575;&#1604;&#1593;&#1585;&#1576;&#1610;&#1577;</option>
          <option value="ms">Bahasa Melayu</option>
        </select>
      </label>
    </div>
    <header className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 sm:gap-6 sm:p-6">
      <MenuImage
        src={restaurant.logo_url}
        alt={`${restaurant.name} logo`}
        loading="eager"
        fallback={<span
        className="text-2xl font-bold"
        aria-hidden="true"
      >{restaurant.name.slice(0, 1) || 'U'}</span>}
        className="h-20 w-20 shrink-0 rounded-2xl object-contain sm:h-24 sm:w-24"
      />
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{ui.welcome}</p>
        <h1 className="mt-1 break-words text-2xl font-bold sm:text-3xl">{restaurant.name}</h1>
        {restaurant.address && <p className="mt-2 whitespace-pre-line break-words text-sm text-muted-foreground">{restaurant.address}</p>}
      </div>
    </header>
    <section
      aria-label={ui.browseMenu}
      className="mt-6"
    >
      <label
        htmlFor="menu-search"
        className="mb-2 block text-sm font-semibold"
      >{ui.findDish}</label>
      <input
        id="menu-search"
        type="search"
        value={search}
        onChange={event => setSearch(event.target.value)}
        placeholder={ui.searchPlaceholder}
        className="min-h-[48px] w-full rounded-xl border border-input bg-background px-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-600"
      />
      <nav
        aria-label={ui.menuCategories}
        className="mt-4 flex gap-2 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {[{
          id: 'all',
          name: ui.allItems
        }, ...groups].map(group => <button
          key={group.id}
          aria-pressed={category === group.id}
          onClick={() => setCategory(group.id)}
          className={`${buttonClass} shrink-0 ${category === group.id ? 'bg-[#232D4B] text-white' : 'border border-border bg-card'}`}
        >{localizedField(group, 'name', menuLanguage)}</button>)}
      </nav>
    </section>
    <div className="mt-4 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
      <div className="min-w-0">
        {!visibleGroups.length && <p
          role="status"
          className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground"
        >{catalog.items.length ? ui.noMatches : ui.noItems}</p>}
        {visibleGroups.map(group => <section
          key={group.id}
          aria-label={localizedField(group, 'name', menuLanguage)}
          className="mb-8"
        >
          <h2 className="mb-4 text-xl font-bold">{localizedField(group, "name", menuLanguage)}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            {group.items.map(item => {
              const plainLine = cart.find(line => line.id === lineIdentity(item.id, plainSelection));
              const atLimit = plainLine ? plainLine.quantity >= MAX_QUANTITY : cart.length >= MAX_LINES;
              return <article
                key={item.id}
                className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card"
              >
                <MenuImage
                  src={item.image_url}
                  alt={localizedField(item, 'name', menuLanguage)}
                  className="aspect-[16/10] w-full object-cover"
                />
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="break-words text-lg font-semibold">{localizedField(item, "name", menuLanguage)}</h3>
                  {localizedField(item, "description", menuLanguage) && <p className="mt-1 break-words text-sm leading-relaxed text-muted-foreground">{localizedField(item, "description", menuLanguage)}</p>}
                  <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-4">
                    <span className="text-lg font-bold">{money(item.price, currency)}</span>
                    <button
                      className={primaryClass}
                      aria-label={ui.cartA11y.add.replace('{name}', localizedField(item, "name", menuLanguage))}
                      disabled={atLimit}
                      onClick={() => setCart(current => addCartItem(current, item))}
                    >{atLimit ? ui.limitReached : ui.add}</button>
                  </div>
                  {plainLine && <p className="mt-2 text-sm text-muted-foreground">{plainLine.quantity} {ui.inYourCart}</p>}
                </div>
              </article>;
            })}
          </div>
        </section>)}
      </div>
      <section
        ref={checkoutSectionRef}
        onFocusCapture={() => setCheckoutFocused(true)}
        onBlurCapture={event => {
          if (!event.currentTarget.contains(event.relatedTarget)) setCheckoutFocused(false);
        }}
        aria-labelledby="cart-heading"
        className="rounded-2xl border border-border bg-card p-4 sm:p-5"
      >
        <h2
          id="cart-heading"
          ref={checkoutRef}
          tabIndex={-1}
          className="text-xl font-bold"
        >{ui.cartHeading} <span className="text-muted-foreground">({count})</span></h2>
        {(recovery.record || recovery.error) && <div className="mt-3 space-y-2" aria-live="polite" data-testid="public-menu-recovery">
          {!intentRecord.current && <p role="status">{ui.draft.recoveryNotice}</p>}
          {recovery.error && <p role="alert">{ui.draft[recovery.error]}</p>}
          <p>{ui.draft.recoveryWarning}</p>
          <button type="button" className={`${buttonClass} border border-border`}
            data-testid="public-menu-retry-recovery"
            disabled={!canHandoff || orderType === 'dine_in' || draftBusy || draftReady || draftReview ||
              recovery.record?.state !== 'active' || Boolean(recovery.error) || draftCoolingDown}
            onClick={() => createDraft(true)}>{ui.draft.recoveryRetry}</button>
          <button type="button" className={`${buttonClass} border border-border`}
            data-testid="public-menu-new-order" disabled={draftBusy}
            onClick={startNewOrder}>{ui.draft.recoveryNew}</button>
        </div>}
        {!cart.length ? <p className="mt-3 text-sm text-muted-foreground">{ui.cartEmpty}</p> : <>
          <ul className="mt-2 divide-y divide-border">
            {cart.map(line => <li
              key={line.id}
              className="py-2"
            >
              <p className="break-words font-semibold">{cartDisplayName(line)}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {money(line.catalog_unit_price, currency)} {ui.each}
              </p>
              <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                <div
                  role="group"
                  aria-label={ui.cartA11y.quantityFor.replace('{name}', cartDisplayName(line))}
                  className="flex items-center rounded-xl border border-border"
                >
                  <button
                    className={buttonClass}
                    aria-label={ui.cartA11y.decrease.replace('{name}', cartDisplayName(line))}
                    onClick={() => changeQuantity(line.id, -1)}
                  >−</button>
                  <output
                    aria-label={ui.cartA11y.quantityOf.replace('{name}', cartDisplayName(line))}
                    className="min-w-[32px] text-center font-semibold"
                  >{line.quantity}</output>
                  <button
                    className={buttonClass}
                    aria-label={ui.cartA11y.increase.replace('{name}', cartDisplayName(line))}
                    disabled={line.quantity >= MAX_QUANTITY}
                    onClick={() => changeQuantity(line.id, 1)}
                  >+</button>
                </div>
                <button
                  className={`${buttonClass} text-muted-foreground underline`}
                  aria-label={ui.cartA11y.remove.replace('{name}', cartDisplayName(line))}
                  onClick={() => setCart(current => current.filter(entry => entry.id !== line.id))}
                >{ui.remove}</button>
              </div>
            </li>)}
          </ul>
          <fieldset className="mt-4">
            <legend className="mb-2 font-semibold">{ui.orderQuestion}</legend>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(ORDER_TYPES).map(([value, label]) => <label
                key={value}
                className="cursor-pointer"
              >
                <input
                  className="peer sr-only"
                  type="radio"
                  name="order-type"
                  value={value}
                  checked={orderType === value}
                  onChange={() => {
                    if (value !== orderType) onDestinationChange(null);
                    setOrderType(value);
                    setTableTouched(false);
                    if (value !== 'dine_in') setTable('');
                  }}
                />
                <span className="flex min-h-[48px] items-center justify-center rounded-xl border border-border px-1 text-sm font-semibold peer-checked:border-[#232D4B] peer-checked:bg-[#232D4B] peer-checked:text-white peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-orange-600">{ui.orderTypeLabels[value] || label}</span>
              </label>)}
            </div>
          </fieldset>
          {orderType === 'delivery' && <DeliveryAddressPicker base={base} restaurantId={restaurantId}
            onDestinationChange={onDestinationChange} cooldownUntil={cooldownUntil} onCooldown={onCooldown} language={menuLanguage} />}
          {orderType === 'dine_in' && <div className="mt-4">
            <label
              htmlFor="table-number"
              className="mb-2 block font-semibold"
            >{ui.tableLabel} <span aria-hidden="true">*</span></label>
            <input
              ref={tableRef}
              id="table-number"
              value={table}
              required
              maxLength={40}
              aria-invalid={Boolean(tableError)}
              aria-describedby={tableError ? 'table-hint table-error' : 'table-hint'}
              onBlur={() => setTableTouched(true)}
              onChange={event => setTable(event.target.value)}
              placeholder={ui.tablePlaceholder}
              className="min-h-[48px] w-full rounded-xl border border-input bg-background px-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-600"
            />
            <p
              id="table-hint"
              className="mt-1 text-xs text-muted-foreground"
            >{ui.tableHint}</p>
            {tableError && <p
              id="table-error"
              role="alert"
              className="mt-2 text-sm text-red-700 dark:text-red-300"
            >{tableError}</p>}
          </div>}
          <div
            className="mt-5 border-t border-border pt-4"
            aria-live="polite"
            aria-atomic="true"
          >
            {!orderType && <p className="text-sm text-muted-foreground">{ui.quoteStatus.chooseOrder}</p>}
            {orderType === 'delivery' && !destination && <p>{ui.quoteStatus.chooseAddress}</p>}
            {Date.now() < cooldownUntil && <p role="status">{ui.quoteStatus.cooldown}</p>}
            {expired && <div role="alert">{ui.quoteStatus.expired} <button type="button" disabled={Date.now() < cooldownUntil} onClick={() => setQuoteAttempt(value => value + 1)}>{ui.quoteStatus.refresh}</button></div>}
            {quoteLoading && <p
              role="status"
              className="text-sm text-muted-foreground"
            >{ui.quoteStatus.calculating}</p>}
            {quoteError && <div role="alert">
              <p className="text-sm text-red-700 dark:text-red-300">{localizedQuoteError(quoteError, menuLanguage)}</p>
              <button
                className={`${buttonClass} mt-2 border border-border`}
                disabled={Date.now() < cooldownUntil}
                onClick={() => {
                  rateLimitedQuote.current = null;
                  setQuoteState(null);
                  setQuoteAttempt(value => value + 1);
                }}
              >{ui.quoteStatus.retry}</button>
            </div>}
            {quote && orderType === 'delivery' && <div><p>{quote.delivery_address}</p><p>{ui.deliveryMetrics.distance}: {quote.delivery_distance_km.toFixed(2)} km</p>{quote.delivery_duration_minutes != null && <p>{ui.deliveryMetrics.duration}: {quote.delivery_duration_minutes} {ui.deliveryMetrics.minuteUnit}</p>}</div>}
            {quote && <dl className="space-y-2 text-sm">
              {[
                ['Subtotal', 'subtotal'],
                ['Service Charge', 'service_charge_amount'],
                ['SST', 'tax_amount'],
                ['Delivery Fee', 'delivery_fee'],
                ['Total', 'total_amount'],
              ].map(([label, field]) => <div
                key={field}
                className={`flex justify-between gap-3 ${field === 'total_amount' ? 'border-t border-border pt-3 text-lg font-bold' : ''}`}
              >
                <dt>{ui.quoteLabels[field] || label}</dt>
                <dd>{money(quote[field], quote.currency)}</dd>
              </div>)}
            </dl>}
          </div>
          {!restaurant.whatsapp_number && <p
            role="status"
            className="mt-3 text-sm"
          >{ui.whatsappUnavailable}</p>}
          <button
            className={`${buttonClass} mt-5 w-full bg-[#166534] text-white hover:bg-[#14532d]`}
            disabled={!canHandoff || draftBusy || draftReview || draftReady || (orderType !== 'dine_in' && (!recovery.loaded || draftCoolingDown))}
            data-testid="public-menu-whatsapp-handoff"
            onClick={sendOrder}
          >{ui.orderWhatsapp}</button>
          <p className="mt-2 text-center text-xs leading-relaxed text-muted-foreground">{ui.whatsappHelp}</p>
          <div aria-live="polite" aria-atomic="true">
            {orderType !== 'dine_in' && draftCoolingDown && <p role="status">{ui.draft.cooldown}</p>}
            {draftReady && <button
              type="button"
              className={`${primaryClass} mt-2 inline-block`}
              data-testid="public-menu-continue-whatsapp"
              onClick={continueDraft}
            >{ui.draft.continue}</button>}
            {draftReady && currentDraft.navigation && <p role={currentDraft.navigation === 'navigationFailed' ? 'alert' : 'status'}>{ui.draft[currentDraft.navigation]}</p>}
            {draftBusy && <p role="status" className="mt-2 text-sm">{ui.draft.creating}</p>}
            {currentDraft?.phase === 'error' && <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">{ui.draft[currentDraft.error]}</p>}
            {draftReview && <div role="alert" className="mt-2 text-sm">
              <p>{ui.draft.changed}</p>
              <dl className="mt-2 space-y-2" data-testid="public-menu-commercial-changes">
                {MONEY_FIELDS.map(field => <div key={field}>
                  <dt className="font-semibold">{ui.quoteLabels[field]}</dt>
                  <dd className="break-words">{ui.draft.previous}: {currentDraft.previousQuote.currency} {currentDraft.previousQuote[field].toFixed(2)}</dd>
                  <dd className="break-words">{ui.draft.updated}: {currentDraft.draft.quote.currency} {currentDraft.draft.quote[field].toFixed(2)}</dd>
                </div>)}
              </dl>
              <button
                type="button"
                data-testid="public-menu-accept-draft-total"
                className={`${primaryClass} mt-2 w-full`}
                onClick={acceptDraftTotal}
              >{ui.draft.accept}</button>
            </div>}
          </div>
        </>}
      </section>
    </div>
    <footer className="mt-8 text-center text-xs text-muted-foreground">Powered by UniGate</footer>
    {count > 0 && <aside
      aria-label={ui.cartA11y.summary}
      className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] shadow-lg"
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
        <div aria-live="polite">
          <p className="font-semibold">{count} {count === 1 ? ui.stickyCart.oneItem : ui.stickyCart.manyItems}</p>
          <p className="text-sm text-muted-foreground">
            {quote
              ? money(quote.total_amount, quote.currency)
              : quoteLoading
                ? ui.stickyCart.updating
                : quoteError
                  ? ui.stickyCart.totalUnavailable
                  : orderType === 'delivery' ? ui.stickyCart.deliveryUnavailable : ui.stickyCart.chooseOrder}
          </p>
        </div>
        {!checkoutVisible && !checkoutFocused && <button
          className={primaryClass}
          onClick={() => {
            checkoutRef.current?.scrollIntoView?.({
              block: 'start',
              behavior: 'smooth'
            });
            checkoutRef.current?.focus({
              preventScroll: true
            });
          }}
        >{ui.stickyCart.viewCart}</button>}
      </div>
    </aside>}
  </main>;
}
